import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";
import { CurrencyConfigRes } from "src/store/features/api/generated";

export type DestinationType = "Self-Hosted Wallet" | "Exchange";

export interface AddressTrFields {
  isFirstParty: boolean | null;
  thirdPartyRecipientName?: string;
  destinationType?: DestinationType;
  destinationVasp?: string;
  destinationVaspName?: string;
  walletVerificationReference?: string;
}

export interface AddressTestingState {
  selectedCurrency: CurrencyConfigRes | undefined;
  address: string;
  memo: string;
  /**
   * Step counter. Post-D3 the step layout is:
   *   1 = AddressForm (currency / address / nickname)
   *   2 = AddressTrOwnership (D3.1)
   *   3 = AddressTrKind (D3.2)
   *   4 = AddressVerify (email + 2FA)
   *   5 = AddressSuccess
   */
  step: number;
  modalOpen: boolean;
  tr: AddressTrFields;
}

const emptyTr: AddressTrFields = {
  isFirstParty: null,
};

const initialState: AddressTestingState = {
  selectedCurrency: undefined,
  address: "",
  step: 1,
  memo: "",
  modalOpen: false,
  tr: { ...emptyTr },
};

const AddressTestingSlice = createSlice({
  name: "addressTesting",
  initialState,
  reducers: {
    setSelectedCurrency: (state, action: PayloadAction<CurrencyConfigRes | undefined>) => {
      state.selectedCurrency = action.payload;
    },
    setAddress: (state, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    setMemo: (state, action: PayloadAction<string>) => {
      state.memo = action.payload;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setModalOpen: (state, action: PayloadAction<boolean>) => {
      state.modalOpen = action.payload;
    },
    setTrFields: (state, action: PayloadAction<Partial<AddressTrFields>>) => {
      state.tr = { ...state.tr, ...action.payload };
    },
    resetTrFields: (state) => {
      state.tr = { ...emptyTr };
    },
  },
});

export default AddressTestingSlice;

export const useAddressTestingState = () => useAppSelector((state) => state.addressTesting);

export const {
  setSelectedCurrency,
  setAddress,
  setMemo,
  setStep,
  setModalOpen,
  setTrFields,
  resetTrFields,
} = AddressTestingSlice.actions;
